import { ChevronDown, FileText } from 'lucide-react';
import { useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type {
  CandidateApplicationAnswer,
  CandidateApplicationFileAnswer,
  CandidateAssignment,
} from '@/types/candidates';
import { cn } from '@/lib/utils';

interface CandidateAdditionalFieldsCardProps {
  assignments: CandidateAssignment[];
}

const DEFAULT_OPEN_ROW_THRESHOLD = 8;

export function CandidateAdditionalFieldsCard({
  assignments,
}: CandidateAdditionalFieldsCardProps) {
  const groups = assignments
    .map((a) => ({
      assignmentId: a.id,
      vacancyTitle: a.vacancyTitle,
      answers: a.customFieldAnswers ?? [],
    }))
    .filter((g) => g.answers.length > 0);

  const totalRows = groups.reduce((acc, g) => acc + g.answers.length, 0);
  const [open, setOpen] = useState(totalRows <= DEFAULT_OPEN_ROW_THRESHOLD);

  if (groups.length === 0) return null;

  const showVacancyHeaders = groups.length > 1;

  return (
    <section className="rounded-lg border border-border bg-background p-5">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-2">
          <h2 className="text-base font-medium">Additional Fields</h2>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4 space-y-5">
          {groups.map((group) => (
            <div key={group.assignmentId} className="space-y-1">
              {showVacancyHeaders ? (
                <h3
                  className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  title={group.vacancyTitle}
                >
                  {group.vacancyTitle}
                </h3>
              ) : null}
              <dl className="divide-y divide-border">
                {group.answers.map((answer) => (
                  <AnswerRow key={answer.key} answer={answer} />
                ))}
              </dl>
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

function AnswerRow({ answer }: { answer: CandidateApplicationAnswer }) {
  return (
    <div className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-4 py-2.5">
      {/* The label is the user's own text and had no wrapping rule of its own, so an unbroken
          token spilled out of the 9rem track. Text and URL values carry their own break rule;
          a file name is clamped where it is rendered. */}
      <dt className="wrap-anywhere text-sm font-medium text-muted-foreground">{answer.label}</dt>
      <dd className="text-sm text-foreground">
        <AnswerValue answer={answer} />
      </dd>
    </div>
  );
}

function AnswerValue({ answer }: { answer: CandidateApplicationAnswer }) {
  const { type, value } = answer;
  if (value === null || value === '') {
    return <span className="text-muted-foreground">—</span>;
  }

  if (type === 'CHECKBOX') {
    return <span>{value === true ? 'Yes' : 'No'}</span>;
  }

  if (type === 'FILE' && isFileAnswer(value)) {
    return (
      <a
        href={value.url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex max-w-full items-center gap-1.5 text-primary underline-offset-2 hover:underline"
        title={value.filename}
      >
        <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{value.filename}</span>
      </a>
    );
  }

  if (type === 'URL' && typeof value === 'string') {
    return (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="break-all text-primary underline-offset-2 hover:underline"
      >
        {value}
      </a>
    );
  }

  if (type === 'LONG_TEXT' && typeof value === 'string') {
    return <span className="whitespace-pre-wrap break-words">{value}</span>;
  }

  if (typeof value === 'number') {
    return <span>{value.toLocaleString()}</span>;
  }

  return <span className="break-words">{String(value)}</span>;
}

function isFileAnswer(
  value: CandidateApplicationAnswer['value'],
): value is CandidateApplicationFileAnswer {
  return (
    typeof value === 'object' &&
    value !== null &&
    'url' in value &&
    'filename' in value
  );
}
